import { PeriodType } from 'interfaces/IDictionnariesStore';
import { IAdvert } from 'interfaces/IAdvert';
import { IAdvertItemsPart } from 'interfaces/IAdvertItem';
import { getStringDynamicDate } from 'utils/getStringDate';
import stringifyNumber from 'utils/stringifyNumber';
import AdvertItemDataModel from './AdvertItemDataModel';

type NotRequiredNumber = number | undefined;

class AdvertDataModel {
  protected advert: IAdvert;
  public item: AdvertItemDataModel | undefined;
  public items: AdvertItemDataModel[] | undefined;

  constructor(advert: IAdvert) {
    this.advert = advert;
    if (this.advert.advert_item) {
      this.item = new AdvertItemDataModel(this.advert.advert_item, {
        type: this.advert.advert_item_type,
        isFirstItem: true,
      });
    }
    if (this.advert.advert_items) {
      this.items = this.advert.advert_items.map(
        (el: IAdvertItemsPart, index) =>
          new AdvertItemDataModel(el.transport, {
            type: el.type,
            isFirstItem: index === 0,
          }),
      );
    }
  }

  get isSimpleAdvert(): boolean {
    return !!this.advert.advert_item;
  }

  get isComplexAdvert(): boolean {
    return !!this.advert.advert_items;
  }

  get advertType(): string {
    return this.advert.advert_type;
  }

  get isSale(): boolean {
    return this.advert.advert_type === 'sale';
  }

  get isRent(): boolean {
    return this.advert.advert_type === 'rent';
  }

  get isPurchase(): boolean {
    return this.advert.advert_type === 'purchase';
  }

  get isArchived(): boolean {
    return this.advert.status === 'archived';
  }

  get isDeleted(): boolean {
    return !!this.advert.deleted;
  }

  get advertItemType(): string {
    return this.advert.advert_item_type;
  }

  get isBus(): boolean {
    return this.advert.advert_item_type === 'bus';
  }

  get isExcavator(): boolean {
    return this.advert.advert_item_type === 'excavator';
  }

  get isLoader(): boolean {
    return this.advert.advert_item_type === 'loader';
  }

  get isTractor(): boolean {
    return this.advert.advert_item_type === 'tractor';
  }

  get isSpecialMachinery(): boolean {
    return ['truck_crane', 'excavator', 'dozer', 'loader', 'other_special'].includes(
      this.advert.advert_item_type,
    );
  }

  get id(): string {
    return this.advert._id;
  }

  get price(): number {
    return this.advert.price;
  }

  get url(): string {
    return this.advert.url;
  }

  get photos(): string[] {
    return this.advert.photos || [];
  }

  get isHighlighted(): boolean {
    return !!this.advert.highlighted;
  }

  get isPriority(): boolean {
    return !!this.advert.priority;
  }

  get isOwner(): boolean {
    return !!this.advert.is_owner;
  }

  get isFavorite(): boolean {
    return !!this.advert.is_favorite;
  }

  get vinChecked(): boolean {
    return !!this.advert.vin_checked;
  }

  get withDriver(): boolean {
    return !!this.advert.with_driver;
  }

  get onOrder(): boolean {
    return this.advert.availability === 'on_order';
  }

  get periodType(): PeriodType | undefined {
    return this.advert.period_type;
  }

  get isContractPrice(): boolean {
    return this.advert.period_type === 'contract';
  }

  get viewsCount(): NotRequiredNumber {
    return this.advert.views_count;
  }

  get cityVerbose(): string {
    return this.advert.city_verbose;
  }

  get shortCityVerbose(): string {
    return this.cityVerbose.split(',')[0];
  }

  get leasingPrice(): NotRequiredNumber {
    return this.advert?.quote_data?.monthly_payment?.value;
  }

  // --------- Getting formatted data ---------- //

  get formattedDate(): string {
    return getStringDynamicDate(this.advert.date_publication_from);
  }

  get formattedPrice(): string {
    return stringifyNumber(this.advert.price);
  }
}

export default AdvertDataModel;
